import type { CsvDelimiter, CsvDescription, CsvQuote } from "@reporting/rp-utils-csv";
import { DELIMITERS, QUOTES } from "./types";

/** Leading non-empty lines sampled by detectCsvFormat. */
const DETECT_SAMPLE_LINES = 20;

const NUMERIC_CELL = /^-?\d+([.,]\d+)?$/;

export type CsvLine = { no: number; text: string };

export type CsvLineFormat = Pick<CsvDescription, 'delimiter' | 'quote'>;

/** First header cell that differs from the expected column at the same position. */
export type CsvHeaderIssue = { index: number; found: string | null; expected: string };

/** Data rows whose cell count differs from the expected columns: how many, and where the first one is. */
export type CsvRowIssue = { count: number; firstLine: number; firstFound: number };

export type CsvValidation = {
	header: CsvHeaderIssue | null;
	/** Rows with fewer cells than columns; they cannot be loaded. */
	shortRows: CsvRowIssue | null;
	/** Rows with a non-empty cell beyond the last column; trailing empty cells (a delimiter at the end of the line) do not count. */
	longRows: CsvRowIssue | null;
	/** Parsed data rows (without the header), at most `maxRows` of them. */
	rows: string[][];
};

const noteRow = (issue: CsvRowIssue | null, line: CsvLine, found: number): CsvRowIssue =>
	issue ? { ...issue, count: issue.count + 1 } : { count: 1, firstLine: line.no, firstFound: found };

/** Splits content into non-empty lines keeping their 1-based line numbers. Accepts LF as well as CRLF endings. */
export function splitCsvLines(content: string): CsvLine[] {
	const lines: CsvLine[] = [];
	content.split(/\r?\n/).forEach((text, i) => {
		if (text.trim()) lines.push({ no: i + 1, text });
	});
	return lines;
}

export function parseCsvLine(line: string, format: CsvLineFormat): string[] {
	const result: string[] = [];
	let current = '';
	let inQuote = false;
	for (let i = 0; i < line.length; i++) {
		const ch = line[i];
		if (inQuote) {
			if (ch === format.quote && line[i + 1] === format.quote) {
				current += ch;
				i++;
			} else if (ch === format.quote) {
				inQuote = false;
			} else {
				current += ch;
			}
		} else if (format.quote && ch === format.quote) {
			inQuote = true;
		} else if (ch === format.delimiter) {
			result.push(current);
			current = '';
		} else {
			current += ch;
		}
	}
	result.push(current);
	return result;
}

/**
 * Column name as written in a header cell: without BOM, enclosure quotes and a `|type` suffix
 * (exports used to write `NAME|nvarchar(20)` into the header), lower-cased.
 */
export function normalizeHeaderName(name: string): string {
	return name
		.replace(/^﻿/, '')
		.trim()
		.replace(/^(["'])(.*)\1$/, '$2')
		.replace(/\|.*$/, '')
		.trim()
		.toLowerCase();
}

/** Compares a parsed header row with the expected column names and returns the first mismatch, or null when they agree. */
export function checkCsvHeader(cells: string[], columns: string[]): CsvHeaderIssue | null {
	for (let i = 0; i < columns.length; i++) {
		const cell = cells[i];
		if (cell === undefined || normalizeHeaderName(cell) !== columns[i].trim().toLowerCase()) {
			return { index: i, found: cell ?? null, expected: columns[i] };
		}
	}
	return null;
}

/** Parses the lines with the given format and checks them against the expected columns; data rows are collected up to `maxRows`. */
export function validateCsv(lines: CsvLine[], format: CsvDescription, columns: string[], maxRows = Infinity): CsvValidation {
	const expected = columns.length;
	let header: CsvHeaderIssue | null = null;
	let dataLines = lines;

	if (format.header && lines.length > 0) {
		header = checkCsvHeader(parseCsvLine(lines[0].text, format), columns);
		dataLines = lines.slice(1);
	}

	let shortRows: CsvRowIssue | null = null;
	let longRows: CsvRowIssue | null = null;
	const rows: string[][] = [];
	for (const line of dataLines) {
		const cells = parseCsvLine(line.text, format);
		if (cells.length < expected) {
			shortRows = noteRow(shortRows, line, cells.length);
		} else if (cells.length > expected && cells.slice(expected).some(c => c.trim() !== '')) {
			longRows = noteRow(longRows, line, cells.length);
		}
		if (rows.length < maxRows) rows.push(cells);
	}

	return { header, shortRows, longRows, rows };
}

/**
 * Guesses delimiter, enclosure and header presence of loaded content.
 * - Delimiter: the candidate that splits the sampled lines into a consistent number of cells, preferably
 *   the expected column count (a plain count of the first line used to pick `|` for headers written as `NAME|type`).
 * - Enclosure: the quote every non-empty cell of the first line is wrapped in; none when the line has no quotes at all.
 * - Header: true when the first line contains a known column name, false when it contains a numeric cell
 *   (a data row); otherwise the current setting is kept.
 * Returns only the properties it could decide.
 */
export function detectCsvFormat(content: string, columns: string[], current: CsvDescription): Partial<CsvDescription> {
	const lines = splitCsvLines(content).slice(0, DETECT_SAMPLE_LINES).map(l => l.text);
	if (lines.length === 0) return {};

	const result: Partial<CsvDescription> = {};
	const expected = columns.length;

	let best: { delimiter: CsvDelimiter; score: number; count: number } | null = null;
	const consider = (delimiter: CsvDelimiter) => {
		const counts = lines.map(l => parseCsvLine(l, { delimiter, quote: current.quote }).length);
		const count = counts[0];
		if (count <= 1) return;
		const consistent = counts.every(c => c === count);
		const score = (count === expected ? 2 : 0) + (consistent ? 1 : 0);
		if (!best || score > best.score || (score === best.score && count > best.count)) {
			best = { delimiter, score, count };
		}
	};
	// a space splits ordinary text as well, so it is only a fallback when no real delimiter is present
	DELIMITERS.filter(d => d !== ' ').forEach(consider);
	if (!best) consider(' ');
	const delimiter = best?.delimiter ?? current.delimiter;
	if (best) result.delimiter = delimiter;

	const rawCells = parseCsvLine(lines[0], { delimiter, quote: '' }).map(c => c.trim()).filter(c => c.length > 1);
	const wrapping = QUOTES.find((q): q is CsvQuote => q !== '' && rawCells.length > 0 && rawCells.every(c => c.startsWith(q) && c.endsWith(q)));
	if (wrapping !== undefined) {
		result.quote = wrapping;
	} else if (!/["']/.test(lines[0])) {
		result.quote = '';
	}

	const cells = parseCsvLine(lines[0], { delimiter, quote: result.quote ?? current.quote });
	const names = new Set(columns.map(c => c.trim().toLowerCase()));
	if (cells.some(c => names.has(normalizeHeaderName(c)))) {
		result.header = true;
	} else if (cells.some(c => NUMERIC_CELL.test(c.trim()))) {
		result.header = false;
	}

	return result;
}
